// stream_reply_cards: resting stream cards surface the thread's top-upvalued replies
// (single top-voted fallback) instead of collapsing to a bare title once the
// viewer is caught up. The rollout switch gates a fallthrough inside
// resolveQuestStreamPreviewAnswers, which the render, height-estimation, and
// geometry paths all share — so estimates track the rendered rows by
// construction. Full design + staging: docs/stream-cards-architecture.md.
import { childQuestIsKnownEmpty } from 'components/quests/questTitleNavigation'

export const STREAM_REPLY_CARDS_FEATURE = 'stream_reply_cards'

export function streamReplyCardsEnabled(): boolean {
    return true
}

// Others row: the card's last line — a face pile of participants NOT already visible
// on the card (author + rendered row authors excluded), most recently active first.
// Hidden below two others so a stray single face never earns a whole row.
export const OTHERS_ROW_ESTIMATE_PX = 34
export const OTHERS_ROW_PILE_MAX = 3
const OTHERS_ROW_MIN_OTHERS = 2

export interface QuestStreamOthersRow {
    users: any[]
    total: number
}

const normalizedIds = (value: unknown): string[] =>
    Array.isArray(value)
        ? value.map(id => (id == null ? null : String(id))).filter((id): id is string => !!id)
        : []

export function resolveQuestStreamOthersRow(
    quest: any,
    renderedAnswers: any[],
): QuestStreamOthersRow | null {
    if (!quest || !streamReplyCardsEnabled()) return null

    const sortedAnswers = Array.isArray(quest.sorted_answers) ? quest.sorted_answers : []
    const visibleAnswers = sortedAnswers.filter((answer: any) => answer && !answer.archive)

    const excludedUserIds = new Set<string>()
    const authorId = quest.parent?.user?.id ?? quest.parent?.user_id ?? quest.user_id
    if (authorId != null) excludedUserIds.add(String(authorId))
    ;(renderedAnswers || []).forEach(answer => {
        const id = answer?.user?.id ?? answer?.user_id
        if (id != null) excludedUserIds.add(String(id))
    })

    const replyUsersById = new Map<string, any>()
    visibleAnswers.forEach((answer: any) => {
        const user = answer?.user
        const id = user?.id ?? answer?.user_id
        if (id != null && user) replyUsersById.set(String(id), user)
    })
    ;(Array.isArray(quest.participants) ? quest.participants : []).forEach((user: any) => {
        if (user?.id != null) replyUsersById.set(String(user.id), user)
    })

    const projectedReplyAuthorIds = Array.isArray(quest.feed_card?.reply_author_ids)
        ? normalizedIds(quest.feed_card.reply_author_ids)
        : null
    const replyAuthorIds =
        projectedReplyAuthorIds ||
        visibleAnswers
            .slice()
            .sort(
                (left: any, right: any) =>
                    new Date(right?.created_at || 0).getTime() -
                    new Date(left?.created_at || 0).getTime(),
            )
            .map((answer: any) => answer?.user?.id ?? answer?.user_id)
            .filter((id: any) => id != null)
            .map(String)

    // Feed-card payloads identify actual reply authors separately from `participants`,
    // which also intentionally contains mentioned users. The participant objects still
    // provide preresolved avatars without making mentions eligible for the facepile.
    const seen = new Set<string>()
    const others: any[] = []
    replyAuthorIds.forEach((id: string) => {
        const key = String(id)
        if (excludedUserIds.has(key) || seen.has(key)) return
        seen.add(key)
        others.push(replyUsersById.get(key) || { id })
    })

    if (others.length < OTHERS_ROW_MIN_OTHERS) return null

    const users = others.slice(0, OTHERS_ROW_PILE_MAX)
    return { users, total: others.length }
}

// A branch thread with no replies IS its head post: the title click already navigates
// parent-lens for known-empty child quests (questTitleNavigation.ts), so the card must
// wear the post presentation — Answer's postLens mode — or the card would promise a room
// its own click refuses to open. No upvalue requirement: emptiness alone decides.
export function isPostLensStreamQuest(quest: any): boolean {
    if (!quest || !streamReplyCardsEnabled()) return false
    if (quest.is_system || quest.is_draft) return false

    const parent = quest.parent
    if (!parent) return false
    // System posts keep their dedicated card treatment (and their title clicks no-op).
    if (parent.user?.system && !parent.leaderboard_announcement) return false

    // The post must live in another thread (a branch head, not a root post).
    const homeQuestId = parent.quest_id
    if (!homeQuestId || String(homeQuestId) === String(quest.id)) return false

    // Any known reply id — declared, stream-preview, or feed-card — means the branch has
    // replies (possibly still hydrating): keep the thread-card presentation.
    if (
        normalizedIds(quest.sorted_answer_ids).length > 0 ||
        normalizedIds(quest.__knovStreamPreviewAnswerIds).length > 0 ||
        normalizedIds(quest.feed_card?.preview_answer_ids).length > 0
    ) {
        return false
    }

    // Share the navigation resolver's emptiness rule so look and click agree by
    // construction: declared-empty counts even before hydration; with nothing declared,
    // only a hydrated empty answer list may claim emptiness.
    return childQuestIsKnownEmpty(quest)
}
